import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Dashboard.css';
import { FaSun, FaMoon, FaDoorOpen, FaGlobe, FaSearch, FaCheck, FaChevronDown } from 'react-icons/fa';
import { renderIcon } from '../utils/iconRenderer';
import { countries } from '../data/mockData';

// Import Modules
import OverviewModule from './modules/OverviewModule';
import UsersModule from './modules/UsersModule';
import GroupsModule from './modules/GroupsModule';
import PrivateChatModule from './modules/PrivateChatModule';
import SettingsModule from './modules/SettingsModule';
import AdsModule from './modules/AdsModule';
import CategoriesModule from './modules/CategoriesModule';
import SupportModule from './modules/SupportModule';

const navigation = [
    { id: 'Overview', label: 'Overview', type: 'dashboard' },
    { id: 'Users', label: 'Users', type: 'users_nav' },
    { id: 'Groups', label: 'Groups', type: 'groups_nav' },
    { id: 'PrivateChats', label: 'Private Chats', type: 'private_chats' },
    { id: 'Ads', label: 'Ads', type: 'megaphone' },
    { id: 'Categories', label: 'Categories', type: 'categories' },
    { id: 'Support', label: 'Support', type: 'life-ring' },
    { id: 'Settings', label: 'Settings', type: 'settings' },
];

const Dashboard = ({ onLogout }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [theme, setTheme] = useState('light');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('All');
    const [countrySearch, setCountrySearch] = useState('');
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    // Determine active tab from URL path
    const path = location.pathname.substring(1); // Remove leading slash
    const activeTab = navigation.find(item => item.id === path)?.id || 'Overview';

    useEffect(() => {
        // If the path is empty or doesn't match a tab, default to Overview
        if (location.pathname === '/' || !navigation.find(item => item.id === path)) {
            navigate('/Overview', { replace: true });
        }
    }, [location.pathname, navigate, path]);

    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
    };

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    const handleTabChange = (tabId) => {
        navigate(`/${tabId}`);
    };

    const renderMainContent = () => {
        const commonProps = { selectedCountry };
        switch (activeTab) {
            case 'Overview':
                return <OverviewModule {...commonProps} />;
            case 'Users':
                return <UsersModule {...commonProps} />;
            case 'Groups':
                return <GroupsModule {...commonProps} />;
            case 'PrivateChats':
                return <PrivateChatModule {...commonProps} />;
            case 'Ads':
                return <AdsModule {...commonProps} />;
            case 'Categories':
                return <CategoriesModule {...commonProps} />;
            case 'Support':
                return <SupportModule {...commonProps} />;
            case 'Settings':
                return <SettingsModule
                    {...commonProps}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    onLogout={() => setShowLogoutModal(true)}
                />;
            default:
                return <OverviewModule {...commonProps} />;
        }
    };

    return (
        <div className={`dashboard-new ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            {/* Sidebar */}
            <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-toggle-container">
                    <button className="sidebar-toggle-btn" onClick={toggleSidebar}>
                        {isSidebarCollapsed ? '→' : '←'}
                    </button>
                </div>

                <div className="sidebar-header">
                    <div className="bunchy-logo-container" style={{ position: 'relative', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {[...Array(8)].map((_, i) => (
                            <div
                                key={i}
                                style={{
                                    position: 'absolute',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${['#87CEEB', '#00BFFF', '#1E90FF', '#4169E1', '#00008B', '#000080', '#191970', '#4682B4'][i]} 0%, #333 100%)`,
                                    transform: `rotate(${i * 45}deg) translateY(-14px)`,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            />
                        ))}
                    </div>
                    {!isSidebarCollapsed && <h2>Bunchy</h2>}
                </div>

                <nav className="sidebar-nav">
                    {navigation.map((item) => (
                        <button
                            key={item.id}
                            className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                            onClick={() => handleTabChange(item.id)}
                            title={isSidebarCollapsed ? item.label : ''}
                        >
                            <span className="nav-icon">
                                {renderIcon(item.type, 20)}
                            </span>
                            {!isSidebarCollapsed && <span className="nav-text">{item.label}</span>}
                        </button>
                    ))}
                </nav>

                <div className="sidebar-footer" style={{ padding: '20px', borderTop: '1px solid var(--border-light)' }}>
                    {!isSidebarCollapsed ? (
                        <div className="user-menu" style={{ width: '100%', margin: 0 }}>
                            <div className="user-avatar">
                                {renderIcon('dashboard', 18)}
                            </div>
                            <div className="user-info">
                                <span className="user-name">Admin User</span>
                                <span className="user-role">Super Admin</span>
                            </div>
                            <button
                                className="logout-mini-btn"
                                onClick={() => setShowLogoutModal(true)}
                                title="Logout"
                                style={{
                                    marginLeft: 'auto',
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    color: '#ef4444',
                                    border: 'none',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <FaDoorOpen size={16} />
                            </button>
                        </div>
                    ) : (
                        <button
                            className="nav-item logout-collapsed"
                            onClick={() => setShowLogoutModal(true)}
                            title="Logout"
                            style={{ color: '#ef4444', border: 'none', background: 'transparent', padding: 0, margin: '0 auto', display: 'flex' }}
                        >
                            <span className="nav-icon">
                                <FaDoorOpen size={20} />
                            </span>
                        </button>
                    )}
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="main-content">
                <header className="top-header">
                    <div className="header-left">
                        <h1>
                            {navigation.find(n => n.id === activeTab)?.label}
                        </h1>
                    </div>
                    <div className="header-right">
                        <div className="country-filter-container-v2">
                            <div className="country-selector-trigger" onClick={() => setShowCountryDropdown(!showCountryDropdown)}>
                                <FaGlobe className="globe-icon" />
                                <span className="selected-country-text">{selectedCountry === 'All' ? 'All Countries' : selectedCountry}</span>
                                <FaChevronDown className={`chevron-icon ${showCountryDropdown ? 'open' : ''}`} />
                            </div>

                            {showCountryDropdown && (
                                <div className="country-dropdown-portal">
                                    <div className="country-search-box">
                                        <FaSearch className="search-icon-small" />
                                        <input
                                            type="text"
                                            placeholder="Search country..."
                                            value={countrySearch}
                                            onChange={(e) => setCountrySearch(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="country-options-list custom-scrollbar">
                                        <div
                                            className={`country-option ${selectedCountry === 'All' ? 'active' : ''}`}
                                            onClick={() => {
                                                setSelectedCountry('All');
                                                setShowCountryDropdown(false);
                                                setCountrySearch('');
                                            }}
                                        >
                                            {selectedCountry === 'All' && <FaCheck className="check-icon" />}
                                            <span>All Countries</span>
                                        </div>
                                        {countries
                                            .filter(c => c.toLowerCase().includes(countrySearch.toLowerCase()))
                                            .map(c => (
                                                <div
                                                    key={c}
                                                    className={`country-option ${selectedCountry === c ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setSelectedCountry(c);
                                                        setShowCountryDropdown(false);
                                                        setCountrySearch('');
                                                    }}
                                                >
                                                    {selectedCountry === c && <FaCheck className="check-icon" />}
                                                    <span>{c}</span>
                                                </div>
                                            ))}
                                        {countries.filter(c => c.toLowerCase().includes(countrySearch.toLowerCase())).length === 0 && (
                                            <div className="no-countries-found">No results</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <button className="theme-toggle" onClick={toggleTheme}>
                            {theme === 'light' ? <FaMoon /> : <FaSun />}
                        </button>
                        <div className="user-menu">
                            <div className="user-avatar">
                                {renderIcon('male', 20)}
                            </div>
                            <div className="user-info">
                                <span className="user-name">Admin</span>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="content-area">
                    {renderMainContent()}
                </div>
            </main>

            {/* Premium Logout Confirmation Modal */}
            {showLogoutModal && (
                <div className="logout-modal-overlay fade-in">
                    <div className="logout-modal-card slide-up">
                        <div className="logout-modal-icon-container">
                            <div className="logout-icon-bg">
                                <FaDoorOpen size={32} />
                            </div>
                        </div>
                        <div className="logout-modal-content">
                            <h3>Confirm Logout</h3>
                            <p>Are you sure you want to end your admin session and sign out? You will need to login again to access the dashboard.</p>
                        </div>
                        <div className="logout-modal-actions">
                            <button
                                className="logout-btn-cancel"
                                onClick={() => setShowLogoutModal(false)}
                            >
                                Stay Logged In
                            </button>
                            <button
                                className="logout-btn-confirm"
                                onClick={() => {
                                    setShowLogoutModal(false);
                                    onLogout();
                                }}
                            >
                                Yes, Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;

